import React, {MouseEvent, useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import styled, {css} from "styled-components";
import {
    ITooltipController,
    TooltipComponent,
    TooltipToggleBehaviorEnum
} from "../../components/tooltip/tooltip.component";
import {CSS_VAR_TOOLTIP_BACKGROUND_COLOR, CSS_VAR_TOOLTIP_COLOR} from "../../components/tooltip/tootip-css-constants";
import {RankingStarIcon} from "../../assets/svg/ranking-star.icon";
import {useServices} from "../../hooks/use-services.hook";
import {TertiaryButton} from "../../basic-components/buttons/tertiary-button";
import {IBundleIncludedSsrViewModel} from "../booking/models/bundle/bundle-included-ssr-view-model.interface";
import {IonIcon} from "@ionic/react";
import {arrowUndoCircleOutline} from "ionicons/icons";
import {DotsAnimationComponent} from "../../components/dots-animation/dots-animation.component";
import {IonSpinnerComponent} from "../../components/spinner/ion-spinner.component";


const SsrTypeInBundleContainerBox = styled.div<{$showUpgradeBorder: boolean}>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    ${CSS_VAR_TOOLTIP_BACKGROUND_COLOR}: ${props => props.theme.colors.primary};
    ${CSS_VAR_TOOLTIP_COLOR}: ${props => props.theme.colors.primaryContrast};
    ${props => props.$showUpgradeBorder && css`
        border: 1px solid ${props => props.theme.colors.tertiary};
        padding: ${props => props.theme.spacing.spacing12};
        border-radius: ${props => props.theme.border.defaultRadius};
    `}

    
    &:last-of-type{
        margin-bottom: 0;
    }
`

const SsrInformationBox = styled.div`
    
    display: flex;
    flex-direction: column;
    padding-left: ${props => props.theme.spacing.spacing8};
    gap: 0;
`

const UpgradeButtonContainerBox = styled.div`
    position: absolute;
    right: ${props => props.theme.spacing.spacing12};
    cursor: pointer;
`

const UpgradeButton = styled(TertiaryButton)`
    position: relative;
    padding: ${props => props.theme.spacing.spacing8} ${props => props.theme.spacing.spacing12};
`

const UpgradeButtonContentBox = styled.div<{$isHidden: boolean}>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    ${props => props.$isHidden && css`
        visibility: hidden;
    `}
`

const TooltipBox = styled(TooltipComponent)`
    ${CSS_VAR_TOOLTIP_BACKGROUND_COLOR}: ${props => props.theme.colors.tertiary};
    ${CSS_VAR_TOOLTIP_COLOR}: ${props => props.theme.colors.tertiaryContrast};
`

const ToolTipContentBox = styled.div`
    background-color: ${props => props.theme.colors.tertiary};
    color: ${props => props.theme.colors.tertiaryContrast};
    padding: ${props => props.theme.spacing.spacing12} ${props => props.theme.spacing.spacing16};
    border-radius: ${props => props.theme.border.defaultRadius};
    text-wrap: wrap;
    white-space: pre-wrap;
    max-width: 300px;
    text-align: center;
    ${props => props.theme.containerMediaQuery.xsAndBelow} {
        max-width: 250px;
    }
`

const DotsAnimation = styled(DotsAnimationComponent)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`

const DowngradeIconContainerBox = styled.div`
    font-size: ${props => props.theme.fontSize.h1};
`

const UpgradeButtonComponent: React.FC<{ssrInBundle: IBundleIncludedSsrViewModel;}> = observer((props) => {
    const services = useServices();
    const [isUpgradingOrDowngrading, setIsUpgradingOrDowngrading] = useState(false);
    const upgradeButtonRef = useRef<HTMLDivElement>(null);
    const tooltipControllerRef = useRef<ITooltipController | null>(null);
    const firstAvailableUpgrade = props.ssrInBundle.availableUpgradesToDisplay[0];
    const hasAvailableUpgrades = Boolean(firstAvailableUpgrade);


    useEffect(() => {
        const timeout = setTimeout(() => {
            if(tooltipControllerRef.current && !props.ssrInBundle.upgradedToSsrType) {
                tooltipControllerRef.current.open();
            }
        }, 300)

        return () => {
            clearTimeout(timeout);
        }

    });

    if(!hasAvailableUpgrades && !props.ssrInBundle.upgradedToSsrType) {
        return null;
    }

    const onClickHandler = async (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        if(isUpgradingOrDowngrading) {
            return false;
        }

        setIsUpgradingOrDowngrading(true);
        try {
            if(props.ssrInBundle.upgradedToSsrType) {
                await props.ssrInBundle.downgrade();
            } else {
                await firstAvailableUpgrade.upgrade();
            }


            tooltipControllerRef.current?.close();
        } finally {
            setIsUpgradingOrDowngrading(false);
        }
    }


    const renderToolTip = () => {
        if(services.booking.current.mutationsManager.isSellBundleMutationInProgress) {
            return null;
        }
        if(!hasAvailableUpgrades && !props.ssrInBundle.upgradedToSsrType) {
            return null;
        }

        if(props.ssrInBundle.availableUpgradesToDisplay.length > 1 && !props.ssrInBundle.upgradedToSsrType) {
            return null;
        }

        const renderToolTipContent = () => {
            //TODO - translations
            if(props.ssrInBundle.upgradedToSsrType) {
                return (<div>
                    <div>Downgrade to:</div>
                    <div>
                        {props.ssrInBundle.originalSsrType.renderNameInBundle(1)}
                    </div>

                </div>);
            } else {

                return (<div>
                    <div>
                        <span>Upgrade la </span> <span>{firstAvailableUpgrade.toSsrType.renderNameInBundle(1)}</span><span> pe nimica toata!</span>
                    </div>
                    <div>
                        E un chilipir!
                    </div>

                </div>);

            }
        }

        return (
            <TooltipBox targetRef={upgradeButtonRef}
                        placement={"top"}
                        toggleBehavior={TooltipToggleBehaviorEnum.OnTargetMouseEnterLeave}
                        tooltipControllerRef={tooltipControllerRef}
                        hideArrow={false}>
                <ToolTipContentBox>
                    {renderToolTipContent()}
                </ToolTipContentBox>
            </TooltipBox>

        );
    }

    const renderButtonContent = () => {

        if(props.ssrInBundle.upgradedToSsrType) {
            if(props.ssrInBundle.allowDowngrade) {
                const renderDowngradeContent = () => {
                    if(isUpgradingOrDowngrading) {
                        return (<IonSpinnerComponent/>)
                    } else {
                        return (<IonIcon icon={arrowUndoCircleOutline}/>);
                    }
                }
                return (
                    <DowngradeIconContainerBox>
                        {renderDowngradeContent()}
                    </DowngradeIconContainerBox>
                );
            } else {
                return null;
            }
        }


        return (
            <UpgradeButton>
                <UpgradeButtonContentBox $isHidden={isUpgradingOrDowngrading}>
                    <RankingStarIcon size={1.2}/>
                    <span>{services.language.translate('Upgrade')}</span>
                </UpgradeButtonContentBox>
                {isUpgradingOrDowngrading && <DotsAnimation/>}

            </UpgradeButton>
        )
    }

    return (
        <UpgradeButtonContainerBox ref={upgradeButtonRef} onClick={onClickHandler}>
            {renderButtonContent()}
            {renderToolTip()}
        </UpgradeButtonContainerBox>
    )

})

interface SsrInBundleComponentProps {
    ssrInBundle: IBundleIncludedSsrViewModel;
}


export const SsrInBundleComponent: React.FC<SsrInBundleComponentProps> = observer((props) => {
    const ssrType = props.ssrInBundle.currentSsrType;
    const availableUpgrades = props.ssrInBundle.availableUpgradesToDisplay;
    const isUpgraded = Boolean(props.ssrInBundle.upgradedToSsrType);
    return (
        <SsrTypeInBundleContainerBox key={ssrType.ssrCode}
                                     $showUpgradeBorder={availableUpgrades.length > 0 || isUpgraded} >
            {ssrType.renderIconInBundle()}
            <SsrInformationBox>
                {ssrType.renderNameInBundle(props.ssrInBundle.quantityToDisplay)}
                {ssrType.renderDetailsInBundle()}
            </SsrInformationBox>
            <UpgradeButtonComponent ssrInBundle={props.ssrInBundle}/>
        </SsrTypeInBundleContainerBox>
    );
})